'use client';
import { useState } from 'react';
import { Alert } from 'antd';
import { useFormik } from 'formik';
import { forgotSchema } from './Schema';
import { ForgotPasswordPayload } from '@/types/Auth';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { TextInput } from '../../Shared/Form/TextInput';
import Button from '../../Shared/Form/Button';
import { useNotification } from '../../Shared/Notification';
import api from '@/utils/axios/api';
import { Color } from '@/utils/constants';
import { showFormError } from '@/utils/common';
import Link from 'next/link';
import { At } from '@phosphor-icons/react';
import { AuthFormWrapper } from '../AuthFormWrapper';

export default function ForgotPasswordForm() {
  const { openNotificationWithIcon } = useNotification();
  const [error, setError] = useState('');

  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: async (values: ForgotPasswordPayload) => {
      return await api.post('/auth/forgot-password/', {
        email: values.email,
      });
    },
    onSuccess: () => {
      setSuccess(true);
      openNotificationWithIcon(
        'success',
        'A reset link has been sent to your email!',
      );
      setError('');
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const apiError =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Something went wrong';
        setError(apiError);
      } else {
        setError('Something went wrong');
      }
    },
  });

  const formik = useFormik<ForgotPasswordPayload>({
    initialValues: {
      email: '',
    },
    validationSchema: forgotSchema,
    onSubmit: (values) => {
      setError('');
      setSuccess(false);
      mutation.mutate(values);
    },
  });

  return (
    <AuthFormWrapper
      title="Forgot Password?"
      subtitle="We'll send you a link to reset your password to your email"
    >
      <form onSubmit={formik.handleSubmit} className="space-y-3" method="post">
        <div className="w-full mt-3">
          {error && (
            <Alert message={error} type="error" className="my-4 text-sm" />
          )}
          {success && (
            <Alert
              message="A reset link has been sent to your email!"
              type="success"
              className="my-4"
            />
          )}
          <div className="relative">
            <TextInput
              id="email"
              type="email"
              label="Email"
              name="email"
              onChange={formik.handleChange}
              placeholder="Enter your email"
              prefix={<At fontSize={18} color={Color.Primary} />}
              required
              error={showFormError(formik.errors, formik.touched, 'email')}
            />
          </div>
        </div>
        <Button
          className="my-4 w-full"
          loading={mutation.isPending}
          type="primary"
          htmlType="submit"
        >
          Submit
        </Button>
        <Link href="/" className="flex">
          <div className="text-primary text-sm">Back to Login</div>
        </Link>
      </form>
    </AuthFormWrapper>
  );
}
